import { create } from 'zustand';
import { Consultant, DateData, Slot, Pricing, Appointment } from '../types';

interface AppState {
  // Loading states
  loading: {
    consultants: boolean;
    dates: boolean;
    slots: boolean;
    pricing: boolean;
    appointments: boolean;
  };
  
  // Data
  consultants: Consultant[];
  dates: DateData[];
  slots: Slot[];
  pricing: Pricing[];
  appointments: Appointment[];
  
  // Pagination for appointments
  appointmentsMeta: {
    page: number;
    total: number;
    totalPages: number;
    limit: number;
  };
  
  // Selected date for slots
  selectedDate: DateData | null;
  
  // Actions
  setLoading: (key: keyof AppState['loading'], value: boolean) => void;
  setConsultants: (consultants: Consultant[]) => void;
  setDates: (dates: DateData[]) => void;
  setSlots: (slots: Slot[]) => void;
  setPricing: (pricing: Pricing[]) => void;
  setAppointments: (appointments: Appointment[], meta?: any) => void;
  setSelectedDate: (date: DateData | null) => void;
  updateSlotStatus: (slotId: string, updates: Partial<Slot>) => void;
  updateDateStatus: (dateId: string, isActive: boolean) => void;
  updatePricing: (priceId: string, price: number) => void;
  removeAppointment: (appointmentId: string) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  loading: {
    consultants: false,
    dates: false,
    slots: false,
    pricing: false,
    appointments: false,
  },
  
  consultants: [],
  dates: [],
  slots: [],
  pricing: [],
  appointments: [],
  
  appointmentsMeta: {
    page: 1,
    total: 0,
    totalPages: 0,
    limit: 10,
  },
  
  selectedDate: null,
  
  setLoading: (key, value) =>
    set((state) => ({
      loading: { ...state.loading, [key]: value },
    })),
    
  setConsultants: (consultants) => set({ consultants }),
  
  setDates: (dates) => set({ dates }),
  
  setSlots: (slots) => set({ slots }),
  
  setPricing: (pricing) => set({ pricing }),
  
  setAppointments: (appointments, meta) =>
    set({
      appointments,
      appointmentsMeta: meta || get().appointmentsMeta,
    }),
    
  setSelectedDate: (date) => set({ selectedDate: date }),
  
  updateSlotStatus: (slotId, updates) =>
    set((state) => ({
      slots: state.slots.map((slot) =>
        slot._id === slotId ? { ...slot, ...updates } : slot
      ),
    })),
    
  updateDateStatus: (dateId, isActive) =>
    set((state) => ({
      dates: state.dates.map((date) =>
        date._id === dateId ? { ...date, is_active: isActive } : date
      ),
    })),
    
  updatePricing: (priceId, price) =>
    set((state) => ({
      pricing: state.pricing.map((p) =>
        p._id === priceId ? { ...p, price } : p
      ),
    })),
    
  removeAppointment: (appointmentId) =>
    set((state) => ({
      appointments: state.appointments.filter((app) => app._id !== appointmentId),
    })),
}));
